// Shared delivery zone definitions used by the map page and the quote modal.
// All polygon coordinates are [lat, lng] pairs.

// Radar/dispatch origin — I-405 & SR-167 interchange in Renton
export const DELIVERY_CENTER: [number, number] = [47.464, -122.212]

export type DeliveryZone = {
  id: number
  price: string
  fee: number
  color: string
  polygons: number[][][]
}

export const DELIVERY_ZONES: DeliveryZone[] = [
  {
    id: 1,
    price: '+$40',
    fee: 40,
    color: '#b8b55c',
    polygons: [
      [
        [47.4800228, -122.1766336],
        [47.4781524, -122.0089267],
        [47.4774623, -121.9806202],
        [47.4230045, -121.9806202],
        [47.386976, -121.9819851],
        [47.367306, -121.9823593],
        [47.2562263, -121.9790384],
        [47.2577289, -122.0786662],
        [47.3001607, -122.0803267],
        [47.2997853, -122.1766336],
      ],
    ],
  },
  {
    id: 2,
    price: '+$75',
    fee: 75,
    color: '#e18251',
    polygons: [
      [
        [47.5140658, -122.1899961],
        [47.50877, -121.9200626],
        [47.4292698, -121.9206226],
        [47.3299152, -121.8696601],
        [47.1847191, -121.9447038],
        [47.1870027, -122.0264679],
        [47.2364576, -122.0270279],
        [47.2364576, -122.128393],
        [47.2919442, -122.2370384],
        [47.3815119, -122.2336782],
        [47.4125974, -122.208477],
        [47.4758488, -122.207917],
      ],
    ],
  },
  {
    id: 3,
    price: '+$120',
    fee: 120,
    color: '#ba8989',
    polygons: [
      [
        [47.1792812, -121.9380585],
        [47.1495463, -121.9979056],
        [47.1607855, -122.1912266],
        [47.1921126, -122.2761991],
        [47.2018986, -122.35109],
        [47.2067909, -122.4951112],
        [47.2859832, -122.521035],
        [47.3299278, -122.4014974],
        [47.3631061, -122.3410086],
        [47.4294002, -122.3726932],
        [47.4800395, -122.3942964],
        [47.5218774, -122.4086985],
        [47.588943, -122.4288615],
        [47.6141918, -122.3395684],
        [47.6277823, -122.2560361],
        [47.6588329, -122.2473948],
        [47.6530124, -121.9507113],
        [47.6471911, -121.8729399],
        [47.4936648, -121.8743801],
        [47.343592, -121.8225325],
      ],
    ],
  },
  {
    id: 4,
    price: '+$150',
    fee: 150,
    color: '#c98ea3',
    polygons: [
      [
        [47.7344413, -122.4014966],
        [47.7305666, -121.8513359],
        [47.2937979, -121.7591623],
        [47.0774827, -121.9721202],
        [47.075521, -122.3696185],
        [47.1431555, -122.4301074],
        [47.1715559, -122.5482047],
        [47.2028767, -122.6000523],
        [47.2742581, -122.6749433],
        [47.3396879, -122.6907856],
        [47.3787119, -122.6202153],
        [47.3806623, -122.5539656],
        [47.3035651, -122.4963571],
        [47.3670077, -122.372499],
        [47.6064239, -122.4689931],
        [47.7344413, -122.4027434],
      ],
    ],
  },
  {
    id: 5,
    price: '+$180',
    fee: 180,
    color: '#804747',
    polygons: [
      [
        [47.7325037, -122.4056213],
        [47.8273474, -122.4272244],
        [47.8969182, -122.3480128],
        [47.8872612, -121.9245907],
        [47.7634921, -121.8165748],
        [47.7307792, -121.8520601],
        [47.7355102, -122.4031984],
      ],
      [
        [47.2002043, -122.5964399],
        [47.1257036, -122.679485],
        [47.0227998, -122.7048599],
        [47.0015659, -122.5076278],
        [47.0652422, -122.4787927],
        [47.0110042, -122.3819068],
        [47.0125771, -121.9666813],
        [47.3309153, -121.6783303],
        [47.5509045, -121.7267733],
        [47.5438977, -121.8132786],
        [47.2930922, -121.7586528],
        [47.0782508, -121.970088],
        [47.0762783, -121.9866387],
        [47.0757147, -122.3702012],
        [47.1427382, -122.4297837],
        [47.1688483, -122.5479969],
        [47.1994206, -122.5964399],
      ],
    ],
  },
]

// Ray-casting point-in-polygon test
export function isPointInPolygon(
  lat: number,
  lng: number,
  polygon: number[][],
): boolean {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0]
    const yi = polygon[i][1]
    const xj = polygon[j][0]
    const yj = polygon[j][1]
    const intersect =
      yi > lng !== yj > lng && lat < ((xj - xi) * (lng - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

export function getZoneForPosition(
  lat: number,
  lon: number,
): { zone: number; price: string; fee: number; color: string } {
  for (const zone of DELIVERY_ZONES) {
    for (const polygon of zone.polygons) {
      if (isPointInPolygon(lat, lon, polygon)) {
        return {
          zone: zone.id,
          price: zone.price,
          fee: zone.fee,
          color: zone.color,
        }
      }
    }
  }
  return { zone: 0, price: 'Custom', fee: 0, color: '#e8d49e' }
}
